import { PageContainer, InputFiled, TextArea, Spinner } from "@/components";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import {
  useCollegeProfileQuery,
  useUpdateCollegeProfileMutation,
} from "@/hooks/api/college.queries";
import { CollegeProfileUpdateType } from "@/services/college.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

const profileSchema = z.object({
  name: z.string().min(1, "College name is required"),
  email: z.string().email("Invalid email address").nullable(),
  phone: z.string().min(10, "Invalid phone number").nullable(),
  address: z.string().nullable(),
  accreditation: z
    .string()
    .min(1, "Accreditation is required")
    .nullable()
    .or(z.literal("")),
  established_year: z.coerce
    .number()
    .min(1800, "Invalid year")
    .max(new Date().getFullYear())
    .nullable(),
  total_students: z.coerce.number().min(0, "Total students cannot be negative"),
  website: z.string().url("Invalid website URL").nullable().or(z.literal("")),
  description: z
    .string()
    .min(10, "Description should be at least 10 characters")
    .nullable()
    .or(z.literal("")),
});

type ProfileFormType = z.infer<typeof profileSchema>;

const ProfileEdit = () => {
  const navigate = useNavigate();
  const { data: profile, isLoading } = useCollegeProfileQuery();
  const updateMutation = useUpdateCollegeProfileMutation();

  const form = useForm<ProfileFormType>({
    resolver: zodResolver(profileSchema) as unknown as any,
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      address: "",
      accreditation: "",
      established_year: new Date().getFullYear(),
      total_students: 0,
      website: "",
      description: "",
    },
  });

  useEffect(() => {
    if (profile) {
      form.reset({
        name: profile.name || "",
        email: profile.email || "",
        phone: profile.phone || "",
        address: profile.address || "",
        accreditation: profile.accreditation || "",
        established_year: profile.established_year || new Date().getFullYear(),
        total_students: profile.total_students || 0,
        website: profile.website || "",
        description: profile.description || "",
      });
    }
  }, [profile, form]);

  const onSubmit = (data: ProfileFormType) => {
    const payload: CollegeProfileUpdateType = {
      accreditation: data.accreditation || "",
      established_year: data.established_year || 0,
      total_students: data.total_students || 0,
      website: data.website || "",
      description: data.description || "",
      name: data.name,
      email: data.email ?? undefined,
      phone: data.phone ?? undefined,
      address: data.address ?? undefined,
    };

    updateMutation.mutate(payload, {
      onSuccess: () => {
        setTimeout(() => {
          navigate("/profile");
        }, 1000);
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <PageContainer className="space-y-6 pb-10">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/profile")}
          className="flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-900"
        >
          <ArrowLeft size={16} />
          <span>Back to Profile</span>
        </button>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-gray-500">Settings</span>
          <h1 className="text-2xl font-bold">Edit College Profile</h1>
        </div>
      </div>

      <div className="bg-card text-card-foreground overflow-hidden rounded-xl border shadow-sm">
        <div className="bg-muted/30 border-b px-6 py-4">
          <h2 className="text-lg font-semibold">General Information</h2>
          <p className="text-muted-foreground text-sm">
            {`Update your college's basic information and public details.`}
          </p>
        </div>

        <div className="p-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                <InputFiled
                  type="text"
                  name="name"
                  label="College Name"
                  placeholder="e.g. Acme College of Engineering"
                  required
                  disabled={updateMutation.isPending}
                />

                <InputFiled
                  type="email"
                  name="email"
                  label="Contact Email"
                  placeholder="admin@college.edu"
                  disabled={updateMutation.isPending}
                />

                <InputFiled
                  type="tel"
                  name="phone"
                  label="Contact Phone"
                  placeholder="e.g. 9876543210"
                  inputMode="numeric"
                  maxLength={10}
                  disabled={updateMutation.isPending}
                />

                <div className="md:col-span-2 lg:col-span-3">
                  <InputFiled
                    type="text"
                    name="address"
                    label="Address"
                    placeholder="Full college address"
                    disabled={updateMutation.isPending}
                  />
                </div>

                <InputFiled
                  type="text"
                  name="accreditation"
                  label="Accreditation"
                  placeholder="e.g. NAAC A++, NBA"
                  required
                  disabled={updateMutation.isPending}
                />

                <InputFiled
                  type="number"
                  name="established_year"
                  label="Established Year"
                  placeholder="e.g. 1995"
                  required
                  disabled={updateMutation.isPending}
                />

                <InputFiled
                  type="number"
                  name="total_students"
                  label="Total Students"
                  placeholder="e.g. 5000"
                  required
                  disabled={updateMutation.isPending}
                />

                <div className="lg:col-span-2">
                  <InputFiled
                    type="url"
                    name="website"
                    label="Website"
                    placeholder="https://www.college.edu"
                    required
                    disabled={updateMutation.isPending}
                  />
                </div>

                <div className="md:col-span-2 lg:col-span-3">
                  <TextArea
                    name="description"
                    label="Description"
                    placeholder="Provide a brief description of the college..."
                    className={{ textareaClass: "min-h-[120px]" }}
                    required
                    disabled={updateMutation.isPending}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={() => navigate("/profile")}
                  disabled={updateMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="lg"
                  disabled={updateMutation.isPending}
                  className="min-w-[150px]"
                >
                  {updateMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </PageContainer>
  );
};

export default ProfileEdit;
